import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { assets } from '../constants/theme';

const steps = [
  {
    num: '01',
    icon: '🌿',
    title: 'Harvest',
    desc: 'Hand-picked at peak ripeness on the volcanic slopes of Mt. Kenya. Our farmers select only the brightest red cherries — the first and most critical step in producing exceptional coffee.',
  },
  {
    num: '02',
    icon: '💧',
    title: 'Process',
    desc: 'Traditional washed processing with 48–72 hour fermentation in mountain spring water. This method defines the bright, clean acidity that makes Kenyan AA world-renowned.',
  },
  {
    num: '03',
    icon: '☀️',
    title: 'Dry & Grade',
    desc: 'Sun-dried on elevated African drying beds for up to 3 weeks. Constantly rotated, then sorted and graded by expert teams — only the best beans make the cut.',
  },
  {
    num: '04',
    icon: '🔥',
    title: 'Roast',
    desc: 'Small-batch precision roasting to showcase each bean\'s unique character. Our master roasters craft specific temperature profiles tailored to each grade\'s ideal expression.',
  },
];

export default function ProcessSection() {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('fadeIn');
      });
    }, { threshold: 0.08 });

    document.querySelectorAll('.proc-fade').forEach((el, i) => {
      el.style.transitionDelay = `${i * 0.12}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const s = {
    section: {
      padding: '8rem 6%',
      backgroundColor: '#1C1007',
      color: '#FAF7F2',
      position: 'relative',
      overflow: 'hidden',
    },
    bgPattern: {
      position: 'absolute', inset: 0,
      backgroundImage: `url('${assets.imgFarm}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      opacity: 0.08,
    },
    inner: { position: 'relative', zIndex: 1, maxWidth: '1240px', margin: '0 auto' },
    header: { textAlign: 'center', marginBottom: '5rem' },
    label: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.75rem', fontWeight: 700,
      letterSpacing: '0.18em', textTransform: 'uppercase',
      color: '#D96D29', marginBottom: '1rem',
    },
    title: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(2.2rem, 4.5vw, 3.4rem)',
      color: '#FAF7F2', lineHeight: 1.12, marginBottom: '1.25rem',
    },
    subtitle: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '1rem', color: 'rgba(250,247,242,0.7)',
      lineHeight: 1.7, maxWidth: '540px', margin: '0 auto',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '0',
      marginBottom: '4rem',
    },
    step: (i) => ({
      padding: '2rem 2rem 2.5rem',
      borderLeft: i > 0 ? '1px solid rgba(250,247,242,0.08)' : 'none',
      borderTop: '2px solid transparent',
      transition: 'border-top-color 0.3s ease',
      cursor: 'default',
    }),
    stepNum: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: '0.8rem', color: '#D96D29',
      marginBottom: '1.25rem',
    },
    stepIcon: { fontSize: '2rem', marginBottom: '1rem' },
    stepTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '1.8rem', color: '#FAF7F2',
      marginBottom: '0.75rem',
    },
    stepDesc: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.88rem', color: 'rgba(250,247,242,0.65)',
      lineHeight: 1.75,
    },
    expandToggle: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '0.75rem',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'rgba(250,247,242,0.7)',
      background: 'none', border: '1px solid rgba(250,247,242,0.2)',
      padding: '0.85rem 2rem', borderRadius: '2px',
      cursor: 'pointer', margin: '0 auto 2rem',
      transition: 'all 0.3s ease',
    },
    expandedContent: {
      borderTop: '1px solid rgba(250,247,242,0.08)',
      paddingTop: '3rem',
      marginBottom: '3rem',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '3rem',
      alignItems: 'center',
    },
    expandImg: {
      width: '100%', aspectRatio: '4/3',
      objectFit: 'cover', borderRadius: '2px',
    },
    expandText: { color: 'rgba(250,247,242,0.8)' },
    expandTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '2rem', color: '#FAF7F2',
      marginBottom: '1rem',
    },
    expandBody: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.95rem', lineHeight: 1.8,
      marginBottom: '1.25rem',
    },
    cta: { textAlign: 'center' },
  };

  return (
    <>
      <style>{`
        #process-section .step-card:hover {
          border-top-color: #D96D29 !important;
        }
        #process-section h3 { color: #FAF7F2 !important; }
      `}</style>
      <section id="journey" style={s.section}>
        <div style={s.bgPattern} aria-hidden="true" />
        <div style={s.inner}>
          <div style={s.header}>
            <div className="proc-fade preFade" style={s.label}>Farm to Cup</div>
            <h2 className="proc-fade preFade" style={s.title}>
              Your Cup! Your Day!<br />
              <span style={{ fontStyle: 'italic', color: '#D96D29' }}>The Journey Behind Every Sip</span>
            </h2>
            <p className="proc-fade preFade" style={s.subtitle}>
              Discover the artistry, science, and tradition that goes into crafting
              your perfect morning ritual — from the volcanic soils of Kenya to your cup.
            </p>
          </div>

          <div id="process-section" className="process-steps-grid proc-fade preFade" style={s.grid}>
            {steps.map((step, i) => (
              <div key={i} className="step-card" style={s.step(i)}>
                <div style={s.stepNum}>{step.num}</div>
                <div style={s.stepIcon}>{step.icon}</div>
                <h3 style={s.stepTitle}>{step.title}</h3>
                <p style={s.stepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <button
              style={s.expandToggle}
              onClick={() => setExpanded(!expanded)}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(250,247,242,0.5)'; e.currentTarget.style.color = '#FAF7F2'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(250,247,242,0.2)'; e.currentTarget.style.color = 'rgba(250,247,242,0.7)'; }}
              aria-expanded={expanded}
            >
              {expanded ? '▲ Show Less' : '▼ Read More About Our Process'}
            </button>
          </div>

          {expanded && (
            <div style={s.expandedContent} className="page-hero-content">
              <img src={assets.imgProcess} alt="Coffee processing" style={s.expandImg} loading="lazy" />
              <div style={s.expandText}>
                <h3 style={s.expandTitle}>The Washed Process: Kenya's Signature Method</h3>
                <p style={s.expandBody}>
                  Kenya's celebrated "double fermentation" washed process begins immediately after harvest.
                  Coffee cherries are depulped within hours of picking, then fermented in clean mountain
                  water for 24–72 hours. This fermentation breaks down the mucilage and develops the
                  complex organic acids that give Kenyan coffee its signature bright, wine-like acidity.
                </p>
                <p style={s.expandBody}>
                  After washing, the beans — still enclosed in their parchment — are soaked in clean
                  water for up to 24 more hours (a unique Kenyan step called "conditioning"). This
                  enhances clarity and sweetness. The beans then spend 2–3 weeks drying slowly on
                  raised beds under the equatorial sun, turning regularly to ensure even drying.
                </p>
                <Link to="/process" className="btn-primary">
                  Explore Full Process Page →
                </Link>
              </div>
            </div>
          )}

          <div style={s.cta}>
            <Link to="/process" className="btn-primary">
              Discover the Full Journey →
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
